/**
 * The VACARS5 Beta Tester Agreement, as pilots accept it on the apply page
 * (2026-10-11). The wording is the staff's agreement document; section 8's
 * signature table became the typed name on the form, and testers must be 18
 * or older (decided 2026-10-11: no guardian process).
 *
 * Changing the wording? Bump AGREEMENT_VERSION here AND in
 * backend/beta_apply.py (beta-agreement.test.js checks they match). The server
 * refuses an application signed against another version, and each stored
 * application records the version its pilot accepted.
 */
export const AGREEMENT_VERSION = 1;

export const AGREEMENT_INTRO =
  "FlyVAC is a volunteer hobby group. This is a short, plain-language agreement between FlyVAC and you, " +
  "a volunteer helping test VACARS5 before it is released. In short: keep pre-release builds and unreleased " +
  "features to yourself, test at your own risk, and tell us what breaks.";

// Each section: a heading, then paragraphs (strings) and lists (arrays).
export const AGREEMENT = [
  {
    h: "1. What's confidential",
    body: [
      "\"Beta material\" means anything you receive or see only because you are a tester, including:",
      [
        "Pre-release builds of VACARS5 (installers, updates) and the links or server addresses they come from.",
        "Features, screens, sounds and changes that have not been publicly released or announced.",
        "The beta checklist, beta reports, and anything posted in FlyVAC's private beta channels.",
        "Other testers' names, pilot IDs, flights, logs and reports.",
        "Anything FlyVAC staff tell you is not public yet.",
        "The new myVAC website at myvac.flyvac.org and its updated design.",
      ],
    ],
  },
  {
    h: "2. What you agree to",
    body: [
      "While you are a tester, you will:",
      [
        "Keep beta material to yourself and other FlyVAC testers and staff.",
        "Install beta builds only on your own computer, and not pass on installers, download links or your login.",
        "Share screenshots and recordings of beta builds only in FlyVAC's private beta Discord channel. Don't post " +
          "them, live-stream beta builds, or describe unreleased features anywhere else, including forums, social " +
          "media, other Discord channels and servers, and video sites.",
        "Report problems through the Beta test page in VACARS5 or the private beta channel, not in public channels.",
        "Not try to get around VACARS5's checks, access other pilots' data, or test the FlyVAC servers in ways you " +
          "weren't asked to (for example load testing or probing for security holes). If you stumble on a security " +
          "problem, tell an admin privately and don't share it.",
      ],
    ],
  },
  {
    h: "3. What you can still talk about",
    body: [
      "This agreement doesn't stop you from:",
      [
        "Saying that you are a VACARS5 beta tester.",
        "Discussing anything FlyVAC has already made public, or that becomes public later through no fault of yours.",
        "Sharing anything you already knew before you joined, or learned elsewhere without restriction.",
        "Sharing information when the law requires it.",
      ],
      "If you're not sure whether something is OK to share, ask an admin first.",
    ],
  },
  {
    h: "4. This is test software",
    body: [
      "Beta builds are unfinished and will have bugs. By testing, you accept that:",
      [
        "VACARS5 beta builds are provided as they are, with no promise that they work or are fit for any purpose.",
        "A build may crash, misbehave, or fail to record or file a flight. Logged hours or flights may need to be " +
          "corrected or removed by staff.",
        "You should back up anything you care about (sim settings, add-on configuration) before testing.",
        "FlyVAC and its volunteers are not responsible for any loss or damage from using beta builds, as far as the law allows.",
        "Testing is voluntary and unpaid. You can stop at any time, and FlyVAC can end the beta or your place in it at any time.",
      ],
      "Beta flights are bid and logged on the new myVAC at myvac.flyvac.org, which is separate from the current " +
        "myVAC on vacanada.org. FlyVAC will still credit your beta flights to you.",
      "VACARS5 is a hobby tool for flight simulation only. It must never be used for real-world flying.",
    ],
  },
  {
    h: "5. Your feedback",
    body: [
      "FlyVAC may use your bug reports, suggestions and ideas to improve VACARS5, freely and without paying you or " +
        "crediting you. You keep the right to use your own ideas too. With your permission, FlyVAC may thank you by " +
        "name as a tester.",
    ],
  },
  {
    h: "6. Your data in beta reports",
    body: [
      "A beta report sends FlyVAC your pass/fail results and notes, comments, pilot ID and name, the flight and " +
        "aircraft, the VACARS5 version, and, if you leave it ticked, the VACARS5 log from that flight. The log is " +
        "scrubbed of passwords and login tokens on your computer and again on the server.",
      [
        "Reports are stored on FlyVAC's server and posted to a private beta channel that FlyVAC admins and staff can read.",
        "They are used only to find and fix problems in VACARS5.",
        "You can untick the log before sending, and you can ask an admin to delete your reports at any time.",
      ],
    ],
  },
  {
    h: "7. How long it lasts",
    body: [
      "This agreement starts when you sign it. Your promise to keep beta material confidential lasts until that " +
        "material is publicly released, or 12 months after you stop testing, whichever comes first.",
      "To leave the beta, tell an admin. When you leave or are removed, delete any beta installers and download " +
        "links you have, and leave the private beta channels. You can keep using VACARS5 once it's publicly released " +
        "like any other FlyVAC pilot.",
      "This is a friendly agreement between hobbyists, not an employment or service contract. If something goes " +
        "wrong, we'll talk about it first.",
    ],
  },
  {
    h: "8. Sign-off",
    body: [
      "Beta testers must be 18 or older. By confirming your age and typing your name below, you confirm you've " +
        "read this agreement and agree to it.",
    ],
  },
];

export const SETUP_OPTIONS = [
  { id: "win-msfs", label: "Windows · MSFS", hint: "MSFS 2020 or 2024" },
  { id: "win-xplane", label: "Windows · X-Plane", hint: "X-Plane 11 or 12" },
  { id: "mac-xplane", label: "Mac · X-Plane", hint: "X-Plane 11 or 12 — we especially need you" },
];
